import {
  GridCellParams,
  GridColDef,
  GridEventListener,
  isAutogeneratedRow,
} from "@mui/x-data-grid-premium";
import { Box, CircularProgress } from "@mui/material";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { HeatSeries } from "src/store/api/generatedApi";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  makeColumnGroupingModel,
  makeColumns,
  makeColumnVisibilityModel,
  withHeaderFilters,
} from "./columns";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { useTranslation } from "react-i18next";
import { useHeatsTableAggregation } from "./HeatsTableAggregation";
import { useTenantData } from "contexts/tenant";
import { useHeatsData } from "components/strategyDashboard/heats/context/HeatsDataContext";
import { useHeatsSelection } from "components/strategyDashboard/heats/context/HeatsSelectionContext";
import { useHeatsFilter } from "components/strategyDashboard/heats/context/HeatsFilterContext";
import { useHeatsRowFilter } from "components/strategyDashboard/heats/context/HeatsRowFilterContext";
import { applyHeatFilters } from "./heatsRowFilter";

const SELECTED_HEAT_ROW_CLASS = "selected-heat-row";

export const HeatsTable = () => {
  const { t } = useTranslation();
  const { t: tenantT } = useTenantTranslation();
  const { format } = useNumberSerialiser();
  const formatUnits = useUnitsFormatter(true);
  const {
    data,
    materials,
    isFetching,
    isLoading,
    scrapEyeOnly,
    hasPriceComparison,
  } = useHeatsData();
  const {
    gridApiRef,
    selectedHeatId,
    setSelectedHeatId,
    selectedSummaryName,
    setSelectedSummaryName,
  } = useHeatsSelection();
  const { ignoredMaterials, finalSampleOnly, setFinalSampleOnly } =
    useHeatsFilter();
  const { options, selection } = useHeatsRowFilter();
  const { toLocalDatetime } = useLocalDatetime();
  const { feature_flags, recipe_type } = useTenantData();
  const formatTime = useCallback(
    (date: string) => toLocalDatetime(date, "LT"),
    [toLocalDatetime]
  );

  const [isCostColumnsCollapsed, setIsCostColumnsCollapsed] = useState(true);
  const [isSummaryCollapsed, setIsSummaryCollapsed] = useState(false);

  const columnsRef = useRef<GridColDef<HeatSeries>[] | null>(null);

  const columns = useMemo(() => {
    if (!data || !materials) {
      return null;
    }
    return withHeaderFilters(
      makeColumns(
        data,
        materials,
        t,
        tenantT,
        feature_flags,
        formatTime,
        formatUnits,
        format,
        ignoredMaterials,
        scrapEyeOnly,
        hasPriceComparison,
        finalSampleOnly,
        setFinalSampleOnly,
        isCostColumnsCollapsed,
        selectedSummaryName,
        isSummaryCollapsed,
        recipe_type
      ),
      options
    );
  }, [
    options,
    data,
    materials,
    t,
    tenantT,
    feature_flags,
    formatTime,
    formatUnits,
    format,
    ignoredMaterials,
    scrapEyeOnly,
    hasPriceComparison,
    finalSampleOnly,
    setFinalSampleOnly,
    isCostColumnsCollapsed,
    selectedSummaryName,
    isSummaryCollapsed,
    recipe_type,
  ]);

  if (columns !== null) {
    columnsRef.current = columns;
  }

  const displayColumns = columns ?? columnsRef.current;

  const columnGroupingModel = useMemo(
    () =>
      data === undefined
        ? []
        : makeColumnGroupingModel(
            data,
            t,
            formatUnits,
            selectedSummaryName,
            setSelectedSummaryName,
            isCostColumnsCollapsed,
            () => {
              setIsCostColumnsCollapsed((prev) => !prev);
            },
            hasPriceComparison,
            isSummaryCollapsed,
            () => {
              setIsSummaryCollapsed((prev) => !prev);
            },
            scrapEyeOnly
          ),
    [
      data,
      t,
      formatUnits,
      selectedSummaryName,
      isCostColumnsCollapsed,
      setSelectedSummaryName,
      hasPriceComparison,
      isSummaryCollapsed,
      scrapEyeOnly,
    ]
  );

  const columnVisibilityModel = useMemo(
    () => (data === undefined ? {} : makeColumnVisibilityModel(data)),
    [data]
  );

  const filteredSeries = useMemo(
    () => (data === undefined ? [] : applyHeatFilters(data.series, selection)),
    [data, selection]
  );

  const { aggregationModel, aggregationFunctions } = useHeatsTableAggregation({
    data,
    filteredSeries,
    isCostColumnsCollapsed,
    selectedSummaryName,
    isSummaryCollapsed,
  });

  const handleOnClick: GridEventListener<"cellClick"> = useCallback(
    (params: GridCellParams<HeatSeries>) => {
      if (params.colDef.field !== "_watch" && !isAutogeneratedRow(params.row)) {
        setSelectedHeatId(params.row.heat_id);
      }
    },
    [setSelectedHeatId]
  );

  if (displayColumns === null) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <HotkeyCompliantDataGrid
      apiRef={gridApiRef}
      rows={filteredSeries}
      showCellVerticalBorder={false}
      showColumnVerticalBorder
      columns={displayColumns}
      columnGroupingModel={columnGroupingModel}
      columnVisibilityModel={columnVisibilityModel}
      onCellClick={handleOnClick}
      loading={isLoading || isFetching || columns === null}
      getRowId={(row) => row.heat_id}
      getRowClassName={(params) =>
        params.id === selectedHeatId ? SELECTED_HEAT_ROW_CLASS : ""
      }
      hideFooter
      aggregationModel={aggregationModel}
      aggregationFunctions={aggregationFunctions}
      getRowHeight={() => "auto"}
      columnHeaderHeight={56}
      sx={{
        minHeight: 300,
        "& .MuiDataGrid-columnHeaderTitle": {
          whiteSpace: "normal",
          lineHeight: "1.2rem",
        },
        "& .MuiDataGrid-row": {
          minHeight: "30px !important",
        },
        [`& .${SELECTED_HEAT_ROW_CLASS}`]: { bgcolor: "action.selected" },
        "& .MuiDataGrid-columnHeader": {
          height: "auto !important",
          maxHeight: "none !important",
          alignItems: "flex-start",
        },
        "& .MuiDataGrid-columnHeaders": {
          maxHeight: "none !important",
        },
        "& .MuiDataGrid-pinnedRows .MuiDataGrid-cell": {
          fontWeight: "fontWeightBold",
        },
        "& .MuiDataGrid-pinnedRows .MuiTypography-root": {
          fontWeight: "fontWeightBold",
        },
      }}
    />
  );
};
